import { Type } from "class-transformer";
import { IsNumber, Min } from "class-validator";
import i18n from "../i18n";
import store from "../store";
import { eSoCalculatesAs } from "./absenceTypes-model";
import { AbstractJSONModel } from "./master-model";
import { PositionCategory } from "./positionCategories";
import { startOfDay } from "date-fns";
import { EmploymentHandler } from "@/handlers/employment-handler";
export interface IEmployments {
  id: number;
  start_date: Date;
  end_date: Date;
  rate_of_employment: number;
  employee_id: number;
  position_id: number;
  created_at: Date;
  updated_at: Date;
  holidays_to_hold: number;
  norm_period_index: number;
  seniority_days_to_hold: number;
  sixth_week_days_to_hold: number;
  locked: boolean;
  first_segment_id?: any;
  has_school_hours: boolean;
  other_time_override?: any | null;
  correction: number | null;
  correction_note: string | null;
  so_calculates_as: number;
  // Set by the CICO/HR sync when a differing stop date is staged for the manager's
  // confirmation (NO market). Read-only: never sent back on update.
  unconfirmed_end_date: Date | null;
}

export interface IEmploymentPost {
  start_date: Date;
  end_date: Date;
  rate_of_employment: number;
  employee_id: number;
  position_id: number;
  holidays_to_hold: number;
  norm_period_index: number;
  seniority_days_to_hold: number;
  sixth_week_days_to_hold: number;
  first_segment_id?: any;
  has_school_hours: boolean;
  other_time_override?: any | null;
  correction: number | null;
  correction_note: string | null;
}

export interface IEmploymentPut {
  id: number;
  start_date: Date;
  end_date: Date;
  rate_of_employment: number;
  holidays_to_hold: number;
  seniority_days_to_hold: number;
  sixth_week_days_to_hold: number;
  correction: number | null;
  correction_note: string | null;
}
export interface SplitEmploymentDaysToHold {
  existing_employment: number;
  new_employment: number;
}

export interface SplitWorkHourPool {
  work_hour_pool_id: number;
  existing_employment_hours: number;
  new_employment_hours: number | null;
}

export interface SplitEmployment {
  change_date: string;
  existing_employment_id: number;
  new_employment_rate: number;
  holidays_to_hold: SplitEmploymentDaysToHold;
  sixth_week_days_to_hold: SplitEmploymentDaysToHold;
  seniority_days_to_hold: SplitEmploymentDaysToHold;
  correction: SplitEmploymentDaysToHold;
  correction_note: {
    existing_employment: string;
    new_employment: string;
  };
  work_hour_pools: SplitWorkHourPool[];
}

export class Employment extends AbstractJSONModel implements IEmployments {
  public id = 0;
  @Type(() => Date)
    start_date: Date = new Date();
  @Type(() => Date)
    end_date: Date = new Date();
  @IsNumber()
    rate_of_employment = 1;
  employee_id = -1;
  @IsNumber()
  @Min(0, {
    message: i18n.global.t("employees.positionNotSelected").toString()
  })
    position_id = -1;
  @Type(() => Date)
    created_at: Date = new Date();
  @Type(() => Date)
    updated_at: Date = new Date();
  holidays_to_hold = 0;
  norm_period_index = 0;
  seniority_days_to_hold = 0;
  sixth_week_days_to_hold = 0;
  locked = false;
  first_segment_id: number | null = null;
  has_school_hours = false;
  other_time_override: boolean | null = null;
  correction: number | null = null;
  correction_note: string | null = null;
  so_calculates_as = 0;
  @Type(() => Date)
    unconfirmed_end_date: Date | null = null;

  public constructor(init?: Partial<Employment>) {
    super();
    Object.assign(this, init);
  }

  public convertToPutObject(): IEmploymentPut {
    const e: IEmploymentPut = Object.assign({});
    e.end_date = this.end_date;
    e.holidays_to_hold = this.holidays_to_hold;
    e.id = this.id;
    e.rate_of_employment = this.rate_of_employment;
    e.seniority_days_to_hold = this.seniority_days_to_hold;
    e.sixth_week_days_to_hold = this.sixth_week_days_to_hold;
    e.start_date = this.start_date;
    e.correction = this.correction;
    e.correction_note = this.correction_note;
    return e;
  }

  public convertToPostObject(): IEmploymentPost {
    const e: IEmploymentPost = Object.assign({});
    e.employee_id = this.employee_id;
    e.end_date = this.end_date;
    e.holidays_to_hold = this.holidays_to_hold;
    e.rate_of_employment = this.rate_of_employment;
    e.seniority_days_to_hold = this.seniority_days_to_hold;
    e.sixth_week_days_to_hold = this.sixth_week_days_to_hold;
    e.start_date = this.start_date;
    e.correction = this.correction;
    e.correction_note = this.correction_note;
    e.norm_period_index = this.norm_period_index;
    e.position_id = this.position_id;
    return e;
  }

  isDkOK21(): boolean {
    const positionCategory: PositionCategory | undefined = store.getters[
      "positions/GetPositionCategoryByPositionId"
    ](this.position_id);
    return positionCategory?.isDkOK21() ?? false;
  }

  isDanishAgreement(): boolean {
    const positionCategory: PositionCategory | undefined = store.getters[
      "positions/GetPositionCategoryByPositionId"
    ](this.position_id);
    return positionCategory?.isDanishAgreement() ?? false;
  }

  isNorwegianAgreement(): boolean {
    const positionCategory: PositionCategory | undefined = store.getters[
      "positions/GetPositionCategoryByPositionId"
    ](this.position_id);
    return positionCategory?.isNorwegianAgreement() ?? false;
  }

  get soCalculatesAsNotSet() {
    return this.so_calculates_as === eSoCalculatesAs.NOT_SET;
  }

  get soCalculatesAsPlannedWorkhours() {
    return this.so_calculates_as === eSoCalculatesAs.PLANNEDWORKHOURS;
  }

  get soCalculatesAsNormDay() {
    return this.so_calculates_as === eSoCalculatesAs.NORMDAY;
  }

  get soConfigurableCalculation() {
    return startOfDay(this.start_date) >= EmploymentHandler.senorityChangeDate;
  }

  get isExistingEmployment() {
    return this.id > 0;
  }

  get isNewEmployment() {
    return this.id < 1;
  }

  equals(other: IEmployments): boolean {
    return other.id === this.id;
  }

  copyEmployment() {
    const newEmployment = new Employment();
    newEmployment.id = this.id;
    newEmployment.start_date = this.start_date;
    newEmployment.end_date = this.end_date;
    newEmployment.rate_of_employment = this.rate_of_employment;
    newEmployment.employee_id = this.employee_id;
    newEmployment.position_id = this.position_id;
    newEmployment.created_at = this.created_at;
    newEmployment.updated_at = this.updated_at;
    newEmployment.holidays_to_hold = this.holidays_to_hold;
    newEmployment.norm_period_index = this.norm_period_index;
    newEmployment.seniority_days_to_hold = this.seniority_days_to_hold;
    newEmployment.sixth_week_days_to_hold = this.sixth_week_days_to_hold;
    newEmployment.locked = this.locked;
    newEmployment.first_segment_id = this.first_segment_id;
    newEmployment.has_school_hours = this.has_school_hours;
    newEmployment.other_time_override = this.other_time_override;
    newEmployment.correction = this.correction;
    newEmployment.correction_note = this.correction_note;
    newEmployment.so_calculates_as = this.so_calculates_as;
    newEmployment.unconfirmed_end_date = this.unconfirmed_end_date;

    return newEmployment;
  }
}

export class CreateEmploymentWithWorkHourPoolsPostback extends AbstractJSONModel {
  @Type(() => Employment)
    employment!: Employment;
  work_hour_pool_info!: string[];
}
